import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmployerPageShell from '../components/employer/EmployerPageShell';
import SimButton from '../components/ui/SimButton';
import api from '../services/api';
import SimCard from '../sim/ui/SimCard';
import SimShell from '../sim/ui/SimShell';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';

type IntegrityLevel = 'HIGH' | 'MEDIUM' | 'LOW';
type SortMode = 'newest' | 'integrity' | 'score';
type IntegrityFilter = 'ALL' | IntegrityLevel;

type EmployerResultItem = {
  completed_at: string;
  candidate: {
    full_name: string;
    sa_id_last4: string;
  };
  performance: {
    percent: number;
    band: string;
  };
  integrity: {
    level: IntegrityLevel;
    flags: string[];
    summary: string[];
  };
  link: {
    job_title: string | null;
  };
  report: {
    report_url: string;
  };
};

type EmployerResultsResponse = {
  items: EmployerResultItem[];
  nextCursor: string | null;
  hasMore: boolean;
};

const isDev = import.meta.env.DEV;

const integrityBadgeClass: Record<IntegrityLevel, string> = {
  HIGH: 'inline-flex items-center rounded-full border border-emerald-300/70 bg-emerald-400/20 px-3 py-1 text-xs uppercase tracking-[0.16em] text-[#0f1a1a]',
  MEDIUM: 'inline-flex items-center rounded-full border border-amber-300/70 bg-amber-400/20 px-3 py-1 text-xs uppercase tracking-[0.16em] text-[#0f1a1a]',
  LOW: 'inline-flex items-center rounded-full border border-rose-300/70 bg-rose-400/20 px-3 py-1 text-xs uppercase tracking-[0.16em] text-[#0f1a1a]',
};

const formatCompletedAt = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
};

const sortItems = (items: EmployerResultItem[], sortMode: SortMode): EmployerResultItem[] => {
  const sorted = [...items];

  if (sortMode === 'score') {
    sorted.sort((a, b) => b.performance.percent - a.performance.percent);
    return sorted;
  }

  if (sortMode === 'integrity') {
    const order: Record<IntegrityLevel, number> = { LOW: 0, MEDIUM: 1, HIGH: 2 };
    sorted.sort((a, b) => {
      const delta = order[a.integrity.level] - order[b.integrity.level];
      if (delta !== 0) return delta;
      return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime();
    });
    return sorted;
  }

  sorted.sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime());
  return sorted;
};

const EmployerSimResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<EmployerResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [integrityFilter, setIntegrityFilter] = useState<IntegrityFilter>('ALL');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestIdRef = useRef(0);
  const requestControllerRef = useRef<AbortController | null>(null);
  const simulationCode =
    searchParams.get('simulationCode')?.trim().length
      ? (searchParams.get('simulationCode') as string).trim()
      : null;

  const loadResults = useCallback(async (cursor: string | null = null, append = false) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const timingLabel = `sim-employer-results-fetch:${requestId}`;
    if (isDev) console.time(timingLabel);

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setNextCursor(null);
      setHasMore(false);
    }
    setError(null);
    try {
      const response = await api.get<EmployerResultsResponse>('/sim/employer/results', {
        params: {
          limit: 25,
          ...(simulationCode ? { simulationCode } : {}),
          ...(cursor ? { cursor } : {}),
        },
        signal: controller.signal,
      });
      if (requestIdRef.current !== requestId) return;
      const responseItems = Array.isArray(response.data?.items) ? response.data.items : [];
      setItems((previous) =>
        append
          ? [
              ...previous,
              ...responseItems.filter(
                (item) => !previous.some((existing) => existing.report.report_url === item.report.report_url)
              ),
            ]
          : responseItems
      );
      setNextCursor(response.data?.nextCursor ?? null);
      setHasMore(Boolean(response.data?.hasMore));
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      if (requestIdRef.current !== requestId) return;
      const message = err?.response?.data?.error?.message || 'Failed to load simulation results.';
      setError(message);
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
        setLoadingMore(false);
      }
      if (isDev) console.timeEnd(timingLabel);
    }
  }, [simulationCode]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || !nextCursor || loading || loadingMore) return;
    loadResults(nextCursor, true);
  }, [hasMore, loading, loadingMore, nextCursor, loadResults]);

  useEffect(() => {
    loadResults();
    return () => {
      requestControllerRef.current?.abort();
    };
  }, [loadResults]);

  const visibleItems = useMemo(() => {
    const filtered =
      integrityFilter === 'ALL'
        ? items
        : items.filter((item) => item.integrity.level === integrityFilter);
    return sortItems(filtered, sortMode);
  }, [integrityFilter, items, sortMode]);

  const handleCopyLink = async (reportUrl: string) => {
    const absoluteUrl = `${window.location.origin}${reportUrl}`;
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      toast.success('Report link copied.');
    } catch {
      toast.error('Unable to copy link.');
    }
  };

  if (loading) {
    return (
      <EmployerPageShell activeSection="reports">
        <SimShell
          className="!min-h-0 !bg-transparent !px-0 !py-0"
          maxWidthClassName="max-w-[1180px]"
          fixedAction={
            <SimButton onClick={() => navigate('/employer/dashboard')}>
              Back
            </SimButton>
          }
        >
          <section className="space-y-2">
            <SimLabel className="!text-[15px] !font-semibold !tracking-[0.18em] !text-[#0d9488]">Simulations</SimLabel>
            <SimTitle>Results</SimTitle>
            <SimBody>Loading completed simulation attempts...</SimBody>
          </section>
          <SimCard className="space-y-3">
            <div className="h-8 w-full animate-pulse rounded-input bg-slate-700/60" />
            <div className="h-8 w-full animate-pulse rounded-input bg-slate-700/60" />
            <div className="h-8 w-11/12 animate-pulse rounded-input bg-slate-700/60" />
          </SimCard>
        </SimShell>
      </EmployerPageShell>
    );
  }

  if (error) {
    return (
      <EmployerPageShell activeSection="reports">
        <SimShell
          className="!min-h-0 !bg-transparent !px-0 !py-0"
          maxWidthClassName="max-w-[1180px]"
          fixedAction={
            <SimButton onClick={() => navigate('/employer/dashboard')}>
              Back
            </SimButton>
          }
        >
          <section className="space-y-2">
            <SimLabel className="!text-[15px] !font-semibold !tracking-[0.18em] !text-[#0d9488]">Simulations</SimLabel>
            <SimTitle>Results</SimTitle>
          </section>
          <SimCard className="space-y-4">
            <SimBody>{error}</SimBody>
            <div className="flex flex-wrap gap-3">
              <SimButton onClick={loadResults}>Retry</SimButton>
            </div>
          </SimCard>
        </SimShell>
      </EmployerPageShell>
    );
  }

  return (
    <EmployerPageShell activeSection="reports">
      <SimShell
        className="!min-h-0 !bg-transparent !px-0 !py-0"
        maxWidthClassName="max-w-[1180px]"
        fixedAction={
          <SimButton onClick={() => navigate('/employer/dashboard')}>
            Back
          </SimButton>
        }
      >
        <section className="space-y-4">
          <div className="space-y-2">
            <SimLabel className="!text-[15px] !font-semibold !tracking-[0.18em] !text-[#0d9488]">Simulations</SimLabel>
            <SimTitle>Results</SimTitle>
            <SimBody>Completed assessments with performance and integrity snapshots.</SimBody>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1">
              <SimLabel>Sort</SimLabel>
              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value as SortMode)}
                className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              >
                <option value="newest">Newest</option>
                <option value="integrity">Integrity (LOW first)</option>
                <option value="score">Score (High first)</option>
              </select>
            </div>
            <div className="space-y-1">
              <SimLabel>Integrity</SimLabel>
              <select
                value={integrityFilter}
                onChange={(event) => setIntegrityFilter(event.target.value as IntegrityFilter)}
                className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              >
                <option value="ALL">All</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>
        </section>
        {visibleItems.length === 0 ? (
          <SimCard className="space-y-4">
            <SimTitle className="text-xl">No completed assessments yet</SimTitle>
            <SimBody>Create a simulation link to start collecting candidate results.</SimBody>
            <div className="flex">
              <SimButton onClick={() => navigate('/sim/create-link')}>Create link</SimButton>
            </div>
          </SimCard>
        ) : (
          <>
            <SimCard className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[60rem] text-left text-sm text-txt-secondary">
                  <thead>
                    <tr className="border-b border-white/10 text-xs uppercase tracking-[0.16em] text-txt-muted">
                      <th className="px-3 py-2">Candidate</th>
                      <th className="px-3 py-2">Completed</th>
                      <th className="px-3 py-2">Performance</th>
                      <th className="px-3 py-2">Integrity</th>
                      <th className="px-3 py-2">Flags</th>
                      <th className="px-3 py-2">Job</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((item, index) => {
                      const visibleFlags = item.integrity.flags.slice(0, 3);
                      const extraFlags = Math.max(item.integrity.flags.length - visibleFlags.length, 0);
                      return (
                        <tr key={`${item.report.report_url}-${index}`} className="border-b border-white/10 last:border-b-0">
                          <td className="px-3 py-3">
                            <div className="font-semibold text-txt-primary">{item.candidate.full_name}</div>
                            <div className="text-xs uppercase tracking-[0.16em] text-txt-muted">
                              ID: {item.candidate.sa_id_last4}
                            </div>
                          </td>
                          <td className="px-3 py-3">{formatCompletedAt(item.completed_at)}</td>
                          <td className="px-3 py-3">
                            {item.performance.percent}% • {item.performance.band}
                          </td>
                          <td className="px-3 py-3">
                            <span className={integrityBadgeClass[item.integrity.level]}>{item.integrity.level}</span>
                          </td>
                          <td className="px-3 py-3">
                            {visibleFlags.length === 0 ? (
                              <span className="text-txt-secondary/70">None</span>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {visibleFlags.map((flag) => (
                                  <span key={flag} className="inline-flex items-center rounded-full border border-line bg-surface-card px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-txt-secondary/85">
                                    {flag}
                                  </span>
                                ))}
                                {extraFlags > 0 ? (
                                  <span className="inline-flex items-center rounded-full border border-line bg-surface-card px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-txt-secondary/85">
                                    +{extraFlags}
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3">{item.link.job_title || '—'}</td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-2">
                              <SimButton onClick={() => navigate(item.report.report_url)}>View report</SimButton>
                              <SimButton variant="secondary" onClick={() => handleCopyLink(item.report.report_url)}>
                                Copy link
                              </SimButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </SimCard>

          <div className="space-y-4 md:hidden">
            {visibleItems.map((item, index) => {
              const visibleFlags = item.integrity.flags.slice(0, 3);
              const extraFlags = Math.max(item.integrity.flags.length - visibleFlags.length, 0);
              return (
                <SimCard key={`${item.report.report_url}-mobile-${index}`} className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-txt-primary">{item.candidate.full_name}</p>
                      <p className="text-xs uppercase tracking-[0.16em] text-txt-muted">
                        ID: {item.candidate.sa_id_last4}
                      </p>
                    </div>
                    <span className={integrityBadgeClass[item.integrity.level]}>{item.integrity.level}</span>
                  </div>
                  <p className="text-sm text-txt-secondary">{formatCompletedAt(item.completed_at)}</p>
                  <p className="text-sm text-txt-secondary">
                    {item.performance.percent}% • {item.performance.band}
                  </p>
                  <p className="text-sm text-txt-muted">{item.link.job_title || 'No job title provided'}</p>
                  {visibleFlags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {visibleFlags.map((flag) => (
                        <span key={flag} className="inline-flex items-center rounded-full border border-line bg-surface-card px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-txt-secondary/85">
                          {flag}
                        </span>
                      ))}
                      {extraFlags > 0 ? (
                        <span className="inline-flex items-center rounded-full border border-line bg-surface-card px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-txt-secondary/85">
                          +{extraFlags}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <SimButton onClick={() => navigate(item.report.report_url)}>View report</SimButton>
                    <SimButton variant="secondary" onClick={() => handleCopyLink(item.report.report_url)}>
                      Copy link
                    </SimButton>
                  </div>
                </SimCard>
              );
            })}
          </div>
          {hasMore ? (
            <div className="mt-4">
              <SimButton disabled={loadingMore} onClick={handleLoadMore}>
                {loadingMore ? 'Loading more...' : 'Load more'}
              </SimButton>
            </div>
          ) : null}
          </>
        )}
      </SimShell>
    </EmployerPageShell>
  );
};

export default EmployerSimResultsPage;
